<template>
    <Teleport to="body">
        <div data-label="help" :class="$style.help" v-clickoutside="hideHelpPopover">
            <div :class="[$style.popup, helpPopoverVisible ? $style.show : $style.hide]">
                <header>
                    <template v-if="visibleRecourses">
                        <pt-icon icon="pt-icon--back" @click="visibleRecourses = false"></pt-icon>
                        <span>{{ $t('header.help.recourses') }}</span>
                    </template>

                    <span v-else>{{ $t('guide.help_hi', { name: accountName }) }}👏</span>
                    <pt-icon icon="pt-icon-close" @click="hideHelpPopover"></pt-icon>
                </header>

                <!-- 切换到recourse列表时，保持高度不变 -->
                <div :class="[$style.wrap, visibleRecourses && $style.active]">
                    <!-- 由于列表之间有分割线样式，recourseList放在数组首位 -->
                    <template v-for="(list, $index) in [recourseList, linkList]">
                        <ul :data-label="$index === 0 ? 'recourse' : 'default'" v-for="ul in list">
                            <template v-for="li in ul">
                                <li v-if="li.link" @click="sendEvent(li)">
                                    <a :href="li.link" target="_blank">
                                        <pt-icon :icon="li.icon"></pt-icon>
                                        <span>{{ $t(li.label) }}</span>
                                    </a>
                                </li>
                                <li v-else-if="visibleType(li)" @click="handleLiClick(li)">
                                    <pt-icon :icon="li.icon"></pt-icon>
                                    <span>{{ $t(li.label) }}</span>
                                    <div data-label="chat" v-if="li.key === 'chat'">
                                        <template v-if="chatImg === 'intercom'">
                                            <img
                                                src="https://static.intercomassets.com/avatars/2691314/square_128/photo-1547006079.png"
                                                alt="kumagai@ptmind.com profile"
                                            />
                                            <img
                                                src="https://static.intercomassets.com/avatars/1322308/square_128/%E4%BF%AE%E6%AD%A34-1561949537.jpg"
                                                alt="大内 profile"
                                            />
                                            <img
                                                src="https://static.intercomassets.com/avatars/1014435/square_128/kubotie_photo-1623161411.jpg"
                                                alt="窪田知昭（Kubota_Chiaki） profile"
                                            />
                                        </template>
                                        <template v-else-if="chatImg === 'sendInBlue'">
                                            <img
                                                src="https://ucarecdn.com/3c0f459f-a125-42bb-a8ab-3b2b1ec9b91a/-/crop/216x216/9,9/-/preview/-/resize/200x200/"
                                            />
                                        </template>
                                    </div>
                                    <pt-icon
                                        v-if="li.key !== 'feedback'"
                                        icon="pt-icon--more"
                                    ></pt-icon>
                                </li>
                            </template>
                        </ul>
                    </template>
                </div>
            </div>

            <div :class="$style.start" @click="handleHelpClick">
                <pt-icon
                    icon="pt-icon-arrow-16"
                    v-if="helpPopoverVisible || chatPopoverVisible"
                ></pt-icon>
                <pt-icon icon="pt-icon-question" v-else></pt-icon>
            </div>
        </div>
    </Teleport>
</template>

<script lang="ts">
import clickoutside from '@/components/ptengine-ui/components/directives/click-outside';

export default {
    name: 'Help',
    directives: { clickoutside }
};
</script>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useAccountInfo } from '@models/account/useAccountInfo';
import { useGuideStore } from '@master/store/guide.store';
import { storeToRefs } from 'pinia';
import {
    contact,
    hideIntercomIcon,
    hideSendInBlue,
    showIntercomIcon,
    showSendInBlue
} from '@/assets/services/intercom.service';
import { getWebsiteHost, getHelpCenterHost, getAreaForUrl } from '@/hooks/useHelpLinks';

// interface
interface LinkItem {
    label: string;
    link?: string;
    icon: string;
    key?: string;
    eventName: string;
}

// state
const websiteHref = getWebsiteHost();
const { account } = useAccountInfo();
const chatPopoverVisible = ref(false); //Intercom or SendInBlue

const area = getAreaForUrl();
const linkList = [
    [
        {
            label: 'header.help.hc',
            eventName: 'help_center',
            icon: 'pt-icon-help',
            link:
                getHelpCenterHost() +
                '?utm_source=app&utm_medium=popup&utm_campaign=support&utm_term=helpcenter'
        },
        ...(area === 'JP'
            ? [
                  {
                      label: 'header.help.release_note',
                      eventName: 'product_release',
                      icon: 'pt-icon-productupdates',
                      link:
                          websiteHref +
                          '/blog/product/?utm_source=app&utm_medium=popup&utm_campaign=support&utm_term=productupdates'
                  }
              ]
            : []),
        {
            label: 'guide.start_guide',
            eventName: 'guide_click',
            key: 'guide',
            icon: 'pt-icon-startguide'
        }
    ],
    [
        {
            label: 'header.help.recourses',
            key: 'recourses',
            eventName: 'recourses',
            icon: 'pt-icon-open'
        },
        {
            label: 'header.help.feedback',
            key: 'feedback',
            eventName: 'feedback',
            icon: 'pt-icon-submitfeedback'
        }
    ],
    [
        {
            label: 'header.help.chat',
            key: 'chat',
            eventName: 'chat_support',
            icon: 'pt-icon-chatsupport'
        }
    ]
];
const recourseList = [
    [
        {
            label: 'header.help.blog',
            eventName: 'blog',
            icon: 'pt-icon-blog',
            link:
                websiteHref +
                '/blog?utm_source=app&utm_medium=popup&utm_campaign=support&utm_term=blog'
        },
        {
            label: 'header.help.casestudy',
            eventName: 'case_studies',
            icon: 'pt-icon-casestudies',
            link:
                websiteHref +
                '/casestudies?utm_source=app&utm_medium=popup&utm_campaign=support_resource&utm_term=casestudy'
        },
        {
            label: 'header.help.videos',
            eventName: 'video_resources',
            icon: 'pt-icon-videoresources',
            link:
                area === 'EN'
                    ? 'https://www.youtube.com/channel/UC7cPacc5cBl8Pt_-LFwokig/featured'
                    : 'https://www.youtube.com/channel/UCAt3K4_VLUQm48RjZX0f0hg/featured'
        },
        ...(area === 'JP'
            ? [
                  {
                      label: 'header.help.community',
                      eventName: 'community',
                      icon: 'pt-icon-community',
                      link: import.meta.env.VITE_BBS_URL as string
                  }
              ]
            : [])
    ]
];
const { helpPopoverVisible, percentage, guideVisible } = storeToRefs(useGuideStore());
const { triggerHelpPopover, hideHelpPopover, hideGuidePopover, showGuidePopover, trackEvent } =
    useGuideStore();

const intercomEventBindFlag = ref(false);
const sendInBlueObserver = ref();
const feedbackPopupId = ref(window.ptAppFeedbackPopupId); //ptAppFeedbackPopupId 在 Experience [Feedback] 内的code mode里定义
const visibleRecourses = ref(false);

const accountName = computed(() => {
    const { name, email, firstName } = account.value || {};
    return name || email?.split('@')?.[0];
});

/**
 * 联系人员头像
 */
const chatImg = computed(() => {
    return helpPopoverVisible.value
        ? window.Intercom
            ? 'intercom'
            : window.SibConversations
            ? 'sendInBlue'
            : ''
        : '';
});

// 弹框关闭时，回到默认列表
watch(
    () => helpPopoverVisible.value,
    val => {
        val || (visibleRecourses.value = false);
    }
);

onMounted(() => {
    chatPopoverVisible.value = intercomVisible();
    chatObserver();
    hideIntercomIcon();
    hideSendInBlue();
});

onUnmounted(() => {
    showIntercomIcon();
    showSendInBlue();
});

/**
 * Feedback 选项显示前题是当前已返回 Experience
 * Guide 选项显示的前提是：当前页面可以显示Guide
 */
function visibleType(li: LinkItem) {
    return li.key === 'feedback'
        ? Boolean(feedbackPopupId.value)
        : li.key !== 'guide' || guideVisible.value;
}

/**
 * Intercom Or SendInBlue
 */
function chatObserver() {
    if (window.Intercom && !intercomEventBindFlag.value) {
        intercomEventBindFlag.value = true;

        // 监听显示事件
        window.Intercom('onShow', function () {
            chatPopoverVisible.value = true;
        });

        // 监听隐藏事件
        window.Intercom('onHide', function () {
            chatPopoverVisible.value = false;
        });
    }

    // 由于 SendInBlue 没提供关闭的监听，使用observeDom的方式
    const target = document.querySelector('div#brevo-conversations') as Element;
    if (window.SibConversations && target && !sendInBlueObserver.value) {
        sendInBlueObserver.value = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (
                    mutation.attributeName === 'class' &&
                    !(mutation.target as HTMLElement).classList.contains(
                        'brevo-conversations--expanded'
                    )
                ) {
                    hideSendInBlue();
                    chatPopoverVisible.value = false;
                }
            });
        });
        sendInBlueObserver.value.observe(target, { attributes: true });
    }
}

function intercomVisible() {
    const frame = window.Intercom && document.querySelector('.intercom-messenger-frame');

    if (frame && frame.getBoundingClientRect) {
        const { width, height } = frame.getBoundingClientRect();
        return width > 0 && height > 0;
    }
    return false;
}

function handleLiClick(item: LinkItem) {
    if (item.key === 'recourses') {
        visibleRecourses.value = true;
    } else {
        hideHelpPopover();
    }

    if (item.key === 'chat') {
        chatObserver();
        showSendInBlue();
        contact();
        hideGuidePopover();

        // SendInBlue没有显示的回调函数
        setTimeout(() => {
            if (window.SibConversations) {
                chatPopoverVisible.value = true;
            }
        });
    }

    if (item.key === 'guide') {
        showGuidePopover('helpClick');
    }

    if (item.key === 'feedback') {
        if (feedbackPopupId.value) {
            window.ptengine?.showEngagement?.(feedbackPopupId.value);
        } else {
            contact();
        }
    }

    sendEvent(item);
}

function handleHelpClick() {
    triggerHelpPopover();
    feedbackPopupId.value = window.ptAppFeedbackPopupId;
    trackEvent('help_click');
}

function sendEvent(li: LinkItem) {
    const property: { [key: string]: string } = {
        position: 'help_list'
    };
    if (li.key === 'guide') {
        property['complete_rate'] = percentage.value + '%';
    }
    trackEvent(li.eventName, property);
}
</script>

<style lang="scss" module>
@import '@style/import.scss';

:global {
    /**
    * 调整 Intercom 图标位置
    */
    .intercom-namespace .intercom-messenger-frame {
        bottom: 78px !important;
        right: 28px !important;
    }

    /**
     * 调整 SendInBlue 图标位置
     * SendInBlue 没有关闭会话的api，暂不调整icon底部位置
     */
    .brevo-conversations {
        // bottom: 84px !important;
        right: 28px !important;
    }
}

.help {
    position: fixed;
    z-index: $page-help-index;
    bottom: $page-help-icon-margin-bottom;
    right: $page-help-icon-margin-right;

    .start {
        width: $page-help-icon-width;
        height: $page-help-icon-width;

        cursor: pointer;
        @extend %flex-center;

        background: $pt-black-900;
        box-shadow: 0px 4px 24px 2px rgba(0, 0, 0, 0.1);
        border-radius: 50%;

        svg {
            width: 24px;
            height: 24px;
            fill: $pt-white;
        }
    }
}

.popup {
    position: absolute;
    bottom: 100%;
    right: 0;
    width: 360px;
    margin-bottom: 14px;
    background: $pt-white;
    box-shadow: 0px 8px 30px 4px rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    transform-origin: right bottom;
    transition: width 200ms ease 0s, height 200ms ease 0s, max-height 200ms ease 0s,
        transform 300ms cubic-bezier(0, 1.2, 1, 1) 0s, opacity 83ms ease-out 0s;

    &.show {
        opacity: 1;
    }

    &.hide {
        transform: scale(0);
        opacity: 0;
    }

    &::after {
        position: absolute;
        top: 100%;
        right: calc(#{$page-help-icon-width} / 2 - 6px);
        width: 0;
        height: 0;
        overflow: hidden;
        content: '';
        border-color: $pt-white transparent transparent transparent;
        border-style: solid dashed dashed dashed;
        border-width: 6px;
    }

    header {
        border-bottom: 1px solid $pt-black-30;
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 82px;
        padding: 0 40px 0 16px;

        span {
            flex: 1;
            font-style: normal;
            font-weight: 700;
            font-size: 20px;
            display: flex;
            color: $pt-black-900;
        }

        & > svg {
            cursor: pointer;
            fill: $pt-black-90;

            &:not(:last-of-type) {
                width: 20px;
                height: 20px;
                margin-right: 16px;
            }

            &:last-of-type {
                position: absolute;
                top: 18px;
                right: 16px;
                width: 24px;
                height: 24px;
            }

            &:hover {
                fill: $pt-black-300;
            }
        }
    }

    .wrap {
        position: relative;

        &.active {
            ul[data-label='default'] {
                visibility: hidden;
            }

            ul[data-label='recourse'] {
                display: block;
            }
        }

        > ul {
            list-style: none;
            margin: 8px;
            padding-bottom: 8px;

            &:not(:last-of-type) {
                border-bottom: 1px solid $pt-black-30;
            }

            &[data-label='recourse'] {
                border: none;
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                display: none;
                z-index: 1;
            }

            li {
                height: 40px;
                border-radius: 3px;
                padding: 0 8px;
                display: flex;
                align-items: center;
                font-weight: 500;
                font-size: 14px;
                color: $pt-black-600;
                cursor: pointer;

                &:hover {
                    background: $pt-black-25;
                }

                &[data-key] div[data-label='more'] {
                    display: block;
                }

                span {
                    flex: 1;
                }

                svg:first-of-type {
                    width: 20px;
                    height: 20px;
                    fill: $pt-black-600;
                    margin-right: 4px;
                }

                div[data-label='icon'] svg {
                    width: 20px;
                    height: 20px;
                }

                > div[data-label='chat'] {
                    display: flex;
                    margin-right: 24px;
                }

                > div[data-label='chat'] img {
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                }

                div[data-label='more'] {
                    display: none;
                }

                div[data-label='more'] svg {
                    width: 12px;
                    fill: #8993a4;
                }

                a {
                    color: $pt-black-600;
                    display: flex;
                    align-items: center;
                    width: 100%;
                    height: 100%;
                }
            }
        }
    }
}
</style>
